import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBanner } from '@/components/StatusBanner';
import {
  deletePrompt,
  fetchPromptDefaults,
  listPrompts,
  patchPrompt,
  upsertPrompt,
  type AgentPromptRow,
  type PromptDefaults,
} from '@/lib/agentPromptsApi';
import { canManageSystemPrompts } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

type SlotKey = 'orchestrator' | 'architect' | 'editor' | 'video_reproduction';

const SLOTS: ReadonlyArray<{ key: SlotKey; label: string; hint: string }> = [
  {
    key: 'orchestrator',
    label: 'Orchestrator',
    hint: 'Top-level Ask-FastFrame system prompt. Tokens {{workspace_id}} / {{route}} are substituted at run time.',
  },
  {
    key: 'architect',
    label: 'Workflow architect',
    hint: 'The sub-agent that builds/edits workflow graphs (build_or_edit_workflow).',
  },
  {
    key: 'editor',
    label: 'Video editor',
    hint: 'The sub-agent that builds Remotion compositions. Contains literal braces — keep them.',
  },
  {
    key: 'video_reproduction',
    label: 'Reference video analysis',
    hint: 'What Gemini is asked for when a user attaches a reference video (analyze_reference_video). Its output is the brief the architect builds from — keep it in FastFrame node types.',
  },
];
type Slot = (typeof SLOTS)[number];

const ROWS_KEY = ['admin', 'agent-prompts'] as const;
const DEFAULTS_KEY = ['admin', 'agent-prompts', 'defaults'] as const;

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** A switch built on a checkbox — this app has no switch component. */
function EnabledSwitch(props: { checked: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <input
      type="checkbox"
      role="switch"
      aria-label={props.label}
      checked={props.checked}
      onChange={(event) => props.onChange(event.target.checked)}
      className="h-4 w-4 accent-primary"
    />
  );
}

/**
 * System prompts (moved here from the product's Settings -> Agent). SUPERADMIN
 * ONLY — the nav hides it and the API refuses every other console role; a
 * person who types the URL sees the refusal.
 *
 * One prompt per slot, in force for every model and every agent. A slot with no
 * override runs on the built-in prompt shipped in code (fetched by api-py from
 * agent-gateway). Saving text identical to the built-in default removes the
 * override instead of creating one.
 */
export function SystemPromptsPage() {
  const { data: me } = useMe();
  const allowed = canManageSystemPrompts(me?.role);
  const queryClient = useQueryClient();

  const rowsQuery = useQuery({
    queryKey: ROWS_KEY,
    queryFn: listPrompts,
    enabled: allowed,
    retry: false,
  });
  const defaultsQuery = useQuery({
    queryKey: DEFAULTS_KEY,
    queryFn: fetchPromptDefaults,
    enabled: allowed,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const [editing, setEditing] = useState<Slot | null>(null);
  const [content, setContent] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [resetTarget, setResetTarget] = useState<AgentPromptRow | null>(null);
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);

  const rows = rowsQuery.data ?? [];
  const defaults: PromptDefaults | undefined = defaultsQuery.data;
  const bySlot = (key: string) => rows.find((row) => row.kind === 'system' && row.key === key);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ROWS_KEY, exact: true });

  const save = useMutation({
    mutationFn: async (slot: Slot) => {
      const existing = bySlot(slot.key);
      const matchesDefault =
        defaults !== undefined && content.trim() === (defaults[slot.key] ?? '').trim();
      if (matchesDefault) {
        // Identical to the built-in default = no override needed. Remove an
        // existing one (acts as Reset); if none, create nothing and say so.
        if (existing) await deletePrompt(existing.id);
        return existing
          ? `${slot.label}: matches the default — override removed.`
          : `${slot.label}: already the built-in default — no override created.`;
      }
      await upsertPrompt({ kind: 'system', key: slot.key, title: slot.label, content, enabled });
      return `${slot.label} prompt saved.`;
    },
    onSuccess: (message) => {
      setNotice({ kind: 'success', message });
      setEditing(null);
      void refresh();
    },
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to save') }),
  });

  const toggle = useMutation({
    mutationFn: ({ row, next }: { row: AgentPromptRow; next: boolean }) =>
      patchPrompt(row.id, { enabled: next }),
    onSuccess: () => void refresh(),
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to update') }),
  });

  const reset = useMutation({
    mutationFn: (row: AgentPromptRow) => deletePrompt(row.id),
    onSuccess: () => {
      setNotice({ kind: 'success', message: 'Override removed — the built-in default is back in use.' });
      setResetTarget(null);
      void refresh();
    },
    onError: (err) => {
      setResetTarget(null);
      setNotice({ kind: 'error', message: errorText(err, 'Failed to reset') });
    },
  });

  if (!allowed) {
    return (
      <div>
        <h1 className="text-card-title font-semibold text-fg-primary">System prompts</h1>
        <p className="mt-2 text-body text-fg-secondary">
          System prompts are for the superadmin role only.
        </p>
      </div>
    );
  }

  const openEdit = (slot: Slot) => {
    const row = bySlot(slot.key);
    setNotice(null);
    setEditing(slot);
    // Start from the current effective prompt so editing starts from real text.
    setContent(row?.content ?? defaults?.[slot.key] ?? '');
    setEnabled(row?.enabled ?? true);
  };

  const loadDefault = () => {
    if (!editing) return;
    const text = defaults?.[editing.key];
    // A slot the gateway does not answer for (an older image) leaves the editor
    // alone rather than blanking the prompt somebody is editing.
    if (typeof text === 'string') setContent(text);
    else setNotice({ kind: 'error', message: 'This gateway does not ship a default for that slot yet.' });
  };

  const defaultsReady = defaultsQuery.isSuccess;
  // Until the overrides are known the page makes NO claim about what is in use:
  // an empty list that is really "not loaded" would offer to overwrite an
  // override from the default text.
  const rowsReady = rowsQuery.isSuccess;

  return (
    <div>
      <h1 className="text-card-title font-semibold text-fg-primary">System prompts</h1>
      <p className="mt-1 max-w-2xl text-body text-fg-secondary">
        You can fully replace any of these prompts with your own — hit Override (they run on the
        built-in default until you do). They are fixed slots the agent always needs, so you cannot
        delete them; Reset drops your override and restores the default.
      </p>

      <div className="mt-4 space-y-3">
        {notice && <StatusBanner kind={notice.kind} message={notice.message} />}
        {rowsQuery.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBanner
              kind="error"
              message={`Could not load the overrides: ${errorText(rowsQuery.error, 'unknown error')}`}
            />
            <button
              type="button"
              onClick={() => void rowsQuery.refetch()}
              disabled={rowsQuery.isFetching}
              className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
            >
              {rowsQuery.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}
        {defaultsQuery.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBanner
              kind="error"
              message={`Could not load the built-in defaults: ${errorText(defaultsQuery.error, 'unknown error')}`}
            />
            <button
              type="button"
              onClick={() => void defaultsQuery.refetch()}
              disabled={defaultsQuery.isFetching}
              className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
            >
              {defaultsQuery.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {SLOTS.map((slot) => {
          const row = bySlot(slot.key);
          const builtIn = defaults?.[slot.key];
          let preview: string;
          if (!rowsReady) preview = rowsQuery.isError ? '(overrides unavailable)' : '(loading overrides…)';
          else if (row) preview = row.content;
          else if (defaultsQuery.isError) preview = '(built-in default unavailable)';
          else if (!defaultsReady) preview = '(loading built-in default…)';
          else preview = builtIn ?? '(no built-in default for this slot)';
          return (
            <section key={slot.key} className="rounded-card border border-border bg-surface-field p-4">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-body font-medium text-fg-primary">{slot.label}</h2>
                    <span className="rounded-full bg-surface-brand-subtle px-2 py-0.5 text-micro font-medium text-fg-secondary">
                      {!rowsReady ? 'Unknown' : row ? (row.enabled ? 'Override' : 'Override (disabled)') : 'Built-in default'}
                    </span>
                  </div>
                  <p className="mt-1 text-meta text-fg-secondary">{slot.hint}</p>
                  <p className="mt-2 line-clamp-3 whitespace-pre-wrap rounded-card bg-state-hover p-2 font-mono text-micro text-fg-secondary">
                    {preview}
                  </p>
                  {rowsReady && (
                    <p className="mt-1 text-micro text-fg-hint">
                      Currently in use: {row ? 'current override' : 'built-in default'}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!rowsReady ? (
                    <button type="button" disabled className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary opacity-50">
                      Override
                    </button>
                  ) : row ? (
                    <>
                      <EnabledSwitch
                        checked={row.enabled}
                        label={`${slot.label} override enabled`}
                        onChange={(next) => toggle.mutate({ row, next })}
                      />
                      <button
                        type="button"
                        onClick={() => openEdit(slot)}
                        className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setResetTarget(row)}
                        className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-destructive-soft-foreground hover:bg-state-hover"
                      >
                        Reset
                      </button>
                    </>
                  ) : (
                    // Until the built-in default is known, an empty editor could
                    // save over it: no override can start.
                    <button
                      type="button"
                      onClick={() => openEdit(slot)}
                      disabled={!defaultsReady}
                      className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
                    >
                      Override
                    </button>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Edit ${editing.label} prompt`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
        >
          <div className="flex max-h-full w-full max-w-3xl flex-col rounded-card border border-border bg-surface-field p-6">
            <h2 className="text-h3 font-semibold text-fg-primary">Edit {editing.label} prompt</h2>
            <p className="mt-1 text-meta text-fg-secondary">{editing.hint}</p>
            <div className="mt-4 flex items-center justify-between gap-2">
              <label htmlFor="prompt-content" className="text-meta font-medium text-fg-secondary">
                Prompt content
              </label>
              <button
                type="button"
                onClick={loadDefault}
                disabled={!defaultsReady || save.isPending}
                className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
              >
                Load built-in default
              </button>
            </div>
            <textarea
              id="prompt-content"
              value={content}
              rows={16}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Leave empty and Reset to fall back to the built-in default."
              className="mt-2 min-h-0 w-full flex-1 rounded-card border border-border bg-background p-2 font-mono text-micro text-fg-primary"
            />
            <label className="mt-3 flex items-center gap-2 text-body text-fg-primary">
              <EnabledSwitch checked={enabled} label="Enabled" onChange={setEnabled} />
              Enabled
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                disabled={save.isPending}
                className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => save.mutate(editing)}
                disabled={save.isPending || !content.trim()}
                className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <ConfirmDialog
          title="Reset to built-in default"
          description="Remove this override? The agent will use the shipped default prompt again."
          confirmLabel="Reset"
          busy={reset.isPending}
          onConfirm={() => reset.mutate(resetTarget)}
          onCancel={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}
